import { useId, type ComponentPropsWithoutRef, type Ref } from 'react';

import { ChevronDownIcon } from '@/components/icons';
import { cn } from '@/lib/utils/cn';

import { FIELD_INPUT, FIELD_LABEL, FIELD_STACK, fieldShell } from './field-shell';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<ComponentPropsWithoutRef<'select'>, 'id' | 'children'> {
  label: string;
  options: readonly SelectOption[];
  /** Entrée vide en tête, pour un champ facultatif. */
  placeholder?: string;
  error?: string | undefined;
  hint?: string;
  ref?: Ref<HTMLSelectElement>;
}

/**
 * Liste déroulante native, habillée comme les champs texte.
 *
 * Un `<select>` natif et non une liste maison : on garde gratuitement la
 * navigation au clavier, la recherche à la frappe, le rendu adapté au mobile
 * — un sélecteur plein écran sur téléphone — et l'intégration au formulaire.
 * Les remplaçants maison perdent presque toujours l'un des quatre.
 *
 * Le chevron est dessiné par-dessus, avec `pointer-events-none` : la flèche
 * native varie d'un navigateur à l'autre et ne suit pas la charte.
 */
export const Select = ({
  label,
  options,
  placeholder,
  error,
  hint,
  className,
  ref,
  disabled,
  ...rest
}: SelectProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const hasError = Boolean(error);

  return (
    <div className={FIELD_STACK}>
      <label htmlFor={id} className={FIELD_LABEL}>
        {label}
      </label>

      <div className={fieldShell({ hasError, disabled: disabled ?? false })}>
        <select
          id={id}
          ref={ref}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={(error ?? hint) ? messageId : undefined}
          className={cn(
            FIELD_INPUT,
            // `appearance-none` retire la flèche native, remplacée ci-dessous.
            // `pr-10` réserve sa place pour que le libellé ne passe pas dessous.
            'h-[clamp(2.75rem,5.4vh,3.125rem)] cursor-pointer appearance-none rounded-lg pr-10 pl-4',
            className,
          )}
          {...rest}
        >
          {placeholder === undefined ? null : <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ChevronDownIcon
          aria-hidden
          className="text-navy-500 pointer-events-none absolute right-3.5 text-base"
        />
      </div>

      {error ? (
        <p id={messageId} role="alert" className="text-xs leading-5 text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-ink-500 text-xs leading-5">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
